import type { RouteConfig } from 'vue-router';

import Vue from 'vue';

import { cabinetRoutes as legacyCabinetRoutes } from './legacy-routes';

import { useAccountsStore } from '@/store/accounts';
import { useProfileStore } from '@/store/profile';
import { useSidepanelStore } from '@/store/side-panel';

import SiteFooter from '@/components/common/SiteFooter.vue';
import BaseAppBar from '@/components/widgets/app-bar/BaseAppBar.vue';

const CabinetLayout = () => import('@/layouts/CabinetLayout.vue');
const LoginLayout = () => import('@/features(FSD)/login-layout').then((m) => m.LoginLayout);

const RoleBasedCabinetSidebar = () => import('@/components/cabinet/RoleBasedCabinetSidebar.vue');

const routes: RouteConfig[] = [
  {
    name: 'index',
    path: '/',
    meta: {
      pageTitle: null,
    },
    // перенаправляем на кабинет, там есть проверка на авторизацию, если не авторизован перенапрвит на account/login
    redirect: { name: 'Cabinet' },
  },
  {
    name: 'account',
    path: '/account',
    components: {
      default: LoginLayout,
      bar: BaseAppBar,
    },
    meta: {
      pageTitle: null,
    },
    children: [
      {
        name: 'login',
        path: 'login',
        meta: {
          pageTitle: 'Авторизация',
        },
        component: () => import('@/pages/login').then((m) => m.Login),
      },
      {
        name: 'createAccount',
        path: 'create',
        meta: {
          pageTitle: 'Регистрация',
        },
        component: () => import('@/pages/create-account').then((m) => m.CreateAccount),
      },
      {
        name: 'recovery',
        path: 'recovery',
        meta: {
          pageTitle: 'Восстановления пароля',
        },
        component: () => import('@/pages/pwd-recovery').then((m) => m.PasswordRecovery),
      },
      {
        name: 'quickAccess',
        path: 'quick-access',
        alias: '',
        meta: {
          pageTitle: 'Пользователи',
        },
        component: () => import('@/pages/quick-access').then((m) => m.QuickAccess),
        async beforeEnter(to, from, next) {
          const accountStore = useAccountsStore();
          await accountStore.update();

          nextTick(() => {
            if (!accountStore.rawList.length)
              return next({ name: 'login' });

            next();
          });
        }
      },
      {
        name: 'setCode',
        path: 'set-code',
        meta: {
          pageTitle: 'Установка кода',
        },
        component: () => import('@/pages/set-code').then((m) => m.SetCode),
        beforeEnter(to, from, next) {
          const { isQuickAccessOn, deviceIdSupported, hasPinSet, hasQuickAccessSettings } = useProfileStore();

          if (deviceIdSupported && ((isQuickAccessOn && !hasPinSet) || !hasQuickAccessSettings)) {
            return next();
          }

          next({ name: 'board' });
        }
      },
    ],
  },
  {
    path: '/cabinet',
    name: 'Cabinet',
    meta: {
      pageTitle: null,
      breadcrumbTitle: 'Главная',
    },
    components: {
      default: CabinetLayout,
      bar: () => import('@/components/widgets/app-bar/MainAppBar.vue'),
      footer: SiteFooter,
    },
    async beforeEnter(to, from, next) {
      const profileStore = useProfileStore();

      if (!profileStore.isAuthenticated && profileStore.deviceIdSupported) {
        return next({ name: 'quickAccess', query: { redirectTo: to.fullPath } });
      }

      if (!profileStore.isAuthenticated) {
        return next({ name: 'login', query: { redirectTo: to.fullPath } });
      }

      if (profileStore.deviceIdSupported && ((profileStore.isQuickAccessOn && !profileStore.hasPinSet) || !profileStore.hasQuickAccessSettings)) {
        return next({ name: 'setCode' });
      }

      let finalCompanyId: string | undefined;
      if (!profileStore.roleSide && to.name !== 'pickMemberRole') {
        finalCompanyId = profileStore.findAnyCompanyByRole('investor')?.id || profileStore.findAnyCompanyByRole('borrower')?.id; // сначала ищем инвестора, потом заемщика
        if (!finalCompanyId) {
          return next({ name: 'pickMemberRole' });
        }
      }
      else if (profileStore.roleSide && !profileStore.companyId) {
        finalCompanyId = profileStore.findAnyCompanyByRole(profileStore.roleSide)?.id;
      }

      if (finalCompanyId) {
        await profileStore.switchToCompany({ companyId: finalCompanyId });
      }

      next();
    },
    children: [
      ...legacyCabinetRoutes,
      {
        name: 'pickMemberRole',
        path: 'member-role',
        meta: {
          pageTitle: 'Выбор роли участника',
        },
        component: () => import('@/views/MemberRoleView.vue'),
      },
      {
        name: 'board',
        path: 'board',
        components: {
          default: () => import('@/components/cabinet/RoleBasedCabinetBoard.vue'),
          sidebar: RoleBasedCabinetSidebar,
        },
      },
      {
        path: 'transactions',
        name: 'transactionsList',
        meta: {
          pageTitle: 'Список операций',
        },
        component: () => import('@/pages/TransactionGrid.vue'),
      },
      {
        path: 'projects-market',
        component: () => import('@/pages/ProjectsMarket.vue'),
        meta: {
          role: 'investor',
          pageTitle: 'Рынок',
        },
      },
      {
        path: 'invest-portfolio',
        component: () => import('@/pages/InvestPortfolio.vue'),
        meta: {
          role: 'investor',
          pageTitle: 'Портфель',
        },
      },
      {
        name: 'project-direct',
        path: 'projects/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})/:human?', // project direct url,
        meta: {
          pageTitle: 'Просмотр проекта', // TODO: сюда бы вывести номер проекта
          guestRole: 'investor',
        },
        components: {
          default: () => import('@/views/ProjectView.vue'),
          sidebar: () => import('@/components/project/sidebar/RoleBasedProjectSidebar.vue'),
        },
      },
      {
        name: 'investorRegistration',
        path: 'investor-registration',
        meta: {
          pageTitle: 'Регистрация инвестора',
          role: 'investor',
          hideAppBarNavigation: true,
        },
        component: () => import('@/pages/InvestorRegistration.vue'),
      },
      {
        name: 'investorGetQualified',
        path: 'qualified-investor',
        meta: {
          pageTitle: 'Заявка на квалиф. инвестора',
          role: 'investor',
          hideAppBarNavigation: true,
        },
        component: () => import('@/pages/QualifiedInvestorApplication.vue'),
      },
      {
        name: 'loanApp',
        path: 'loan-app/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?', // direct url to loan app (new or fill)
        alias: 'loan-app/new',
        meta: {
          pageTitle: 'Заполнение заявки', // TODO: сюда бы вывести статус движения заявки?
          role: 'borrower',
          hideAppBarNavigation: true,
        },
        component: () => import('@/pages/loan-application').then((m) => m.LoanApplication),
      },
      {
        path: 'autoinvest-app',
        meta: {
          role: 'investor',
        },
        component: () => import('@/pages/autoinvest').then((m) => m.AutoinvestApplication),
        beforeEnter(to, from, next) {
          const profileStore = useProfileStore();
          const nextRoute = profileStore.companyRegStateCompleted && profileStore.investorAppRegState === 'done' ? undefined : { name: 'board' };
          next(nextRoute);
        },
        children: [
          {
            name: 'autoinvestPickFeature',
            path: '',
            meta: {
              pageTitle: 'Новая заявка на автоинвестирование/автоследование',
              title: 'Новая заявка',
            },
            component: () => import('@/pages/autoinvest').then((m) => m.AutoinvestPickFeature),
          },
          {
            name: 'autoinvestApp',
            path: 'invest/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?',
            alias: 'invest/new',
            meta: {
              pageTitle: 'Новая заявка на автоинвестирование',
              title: 'Автоинвестирование',
            },
            component: () => import('@/pages/autoinvest').then((m) => m.AutofollowForm),
          },
          {
            name: 'autofollowApp',
            path: 'follow/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?',
            alias: 'follow/new',
            meta: {
              pageTitle: 'Новая заявка на автоследование',
              title: 'Автоследование',
            },
            component: () => import('@/pages/autoinvest').then((m) => m.AutofollowForm),
          },
        ],
      },
      {
        name: 'borrowerRegistration',
        path: 'borrower-registration',
        meta: {
          pageTitle: 'Регистрация заемщика',
          role: 'borrower',
          hideAppBarNavigation: true,
        },
        beforeEnter(to, from, next) {
          const profileStore = useProfileStore();

          if (profileStore.borrowerAppRegState === 'done') {
            return next({ name: 'board' });
          }

          return next();
        },
        component: () => import('@/pages/BorrowerRegistration.vue'),
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('@/views/ProfileView.vue'),
      },
      {
        path: 'ticket/:ticketNum',
        alias: 't/:ticketNum',
        beforeEnter(to, from, next) {
          if (to.params.ticketNum) {
            const sidePanelStore = useSidepanelStore();
            sidePanelStore.spawn({
              name: 'Tickets',
              props: {
                ticketNum: to.params.ticketNum,
              },
            });
          }

          return next({ name: 'board' });
        },
      },
    ],
  },
  {
    // таск отправляет свой ид, перенаправляем на страницу проекта инвестора
    path: '/resolve-project/:id',
    async beforeEnter(to, from, next) {
      const { response } = await Vue.http.post('/admin/ext/task/resolve_project_by_id', {
        id: to.params.id,
      });

      const project_id = response?.project_id;
      if (!project_id) {
        Vue.notify({
          type: 'error',
          text: 'Проект не найден',
          duration: -1,
        });
        return;
      }

      return next({ name: 'project-direct', params: { num: response.project_id } });
    },
  },
  /* {
    name: 'getTheApp',
    path: '/get-the-app',
    component: (() => import('@/pages/GetMobileApp.vue')),
  }, */
  {
    path: '*',
    redirect: '/account/quick-access',
  },
];

export { routes as default, routes };
